'use client';

import { useState } from 'react';
import { NavDropdown, NavDropdownItem } from './NavDropdown';
import {
  MONTH_NAMES,
  HUB_YEAR,
  getMonthDisplay,
  getEventTypeDisplay,
  getReligionDisplay,
  EVENT_TYPE_SLUGS,
  RELIGION_SLUGS,
} from '@/lib/hub-pages';
import { uiFb, getCountryNameEntry } from '@/lib/i18n';
import type { CountryCode } from '@/lib/types';

interface NavBrowseLinksProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  lang?: string;
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pb-1 pt-2.5 text-caption font-semibold uppercase tracking-wider text-muted first:pt-1.5">
      {children}
    </div>
  );
}

function SectionDivider() {
  return <div className="mx-3 border-t border-border-subtle" />;
}

const TYPE_ICONS: Record<string, string> = {
  'public-holidays': '\u{1F3DB}\uFE0F',
  'national-days': '\u{1F38C}',
  'observances': '\u{1F441}\uFE0F',
  'cultural-festivals': '\u{1F3AA}',
  'local-events': '\u{1F4CD}',
};

const RELIGION_ICONS: Record<string, string> = {
  'islamic': '\u262A\uFE0F',
  'hindu': '\u{1F549}\uFE0F',
  'lunar': '\u{1F319}',
  'christian': '\u271D\uFE0F',
};

export function NavBrowseLinks({ isOpen, onToggle, onClose, lang = 'en' }: NavBrowseLinksProps) {
  const [showAllMonths, setShowAllMonths] = useState(false);

  const currentMonth = new Date().getMonth();

  const visibleMonthIndices = [
    (currentMonth - 1 + 12) % 12,
    currentMonth,
    (currentMonth + 1) % 12,
  ];

  const monthIndices = showAllMonths
    ? Array.from({ length: 12 }, (_, i) => i)
    : visibleMonthIndices;

  return (
    <NavDropdown
      isOpen={isOpen}
      onToggle={onToggle}
      onClose={onClose}
      align="right"
      label={uiFb('ui.explore_holidays', 'Explore Holidays', lang)}
      wide
      trigger={
        <span className="flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
          <span>{uiFb('ui.explore_holidays', 'Explore Holidays', lang)}</span>
        </span>
      }
    >
      {/* By Month */}
      <SectionHeader>{uiFb('ui.holidays_by_month', 'Holidays by Month', lang)}</SectionHeader>
      {monthIndices.map((i) => (
        <NavDropdownItem
          key={MONTH_NAMES[i]}
          icon={'\u{1F4C5}'}
          label={getMonthDisplay(i + 1, lang)}
          subLabel={i === currentMonth ? uiFb('ui.this_month', 'This month', lang) : undefined}
          isActive={i === currentMonth}
          href={`/${MONTH_NAMES[i]}-${HUB_YEAR}-worldwide-calendar`}
          onClick={onClose}
        />
      ))}
      {!showAllMonths && (
        <button
          onClick={() => setShowAllMonths(true)}
          className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-xs text-brand transition-colors hover:bg-surface-hover"
        >
          <span className="text-base">{'\u00B7\u00B7\u00B7'}</span>
          <span>{uiFb('ui.view_all_months', 'View all months', lang)}</span>
        </button>
      )}
      {showAllMonths && (
        <button
          onClick={() => setShowAllMonths(false)}
          className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-xs text-brand transition-colors hover:bg-surface-hover"
        >
          <span className="text-base">{'\u00B7\u00B7\u00B7'}</span>
          <span>{uiFb('ui.show_less', 'Show less', lang)}</span>
        </button>
      )}

      {/* By Type */}
      <SectionDivider />
      <SectionHeader>{uiFb('ui.nav_by_type', 'By Type', lang)}</SectionHeader>
      {Object.keys(EVENT_TYPE_SLUGS).map((slug) => (
        <NavDropdownItem
          key={slug}
          icon={TYPE_ICONS[slug]}
          label={getEventTypeDisplay(slug, lang)}
          href={`/${slug}-worldwide-calendar`}
          onClick={onClose}
        />
      ))}

      {/* Faith Calendars */}
      <SectionDivider />
      <SectionHeader>{uiFb('ui.nav_faith_calendars', 'Faith Calendars', lang)}</SectionHeader>
      {Object.keys(RELIGION_SLUGS).map((key) => (
        <NavDropdownItem
          key={key}
          icon={RELIGION_ICONS[key]}
          label={`${getReligionDisplay(key, lang)} ${uiFb('hub.calendar', 'Calendar', lang)}`}
          href={`/${key}-calendar-${HUB_YEAR}-worldwide-calendar`}
          onClick={onClose}
        />
      ))}

      {/* Travel & Planning */}
      <SectionDivider />
      <SectionHeader>{uiFb('ui.nav_travel', 'Travel & Planning', lang)}</SectionHeader>
      <NavDropdownItem
        icon={'\u{1F3D6}\uFE0F'}
        label={uiFb('ui.long_weekends', 'Long Weekends', lang)}
        href="/indonesia-long-weekends-2026"
        onClick={onClose}
      />
      <NavDropdownItem
        icon={'\u2708\uFE0F'}
        label={uiFb('ui.best_time', 'Best Time', lang)}
        href="/best-time-to-visit-thailand"
        onClick={onClose}
      />
      <NavDropdownItem
        icon={'\u{1F33F}'}
        label={uiFb('ui.lens_nature', 'Nature', lang)}
        href={`/nature-calendar-${HUB_YEAR}-worldwide-calendar`}
        onClick={onClose}
      />

      {/* Festivals */}
      <SectionDivider />
      <SectionHeader>{uiFb('ui.nav_festivals', 'Festivals', lang)}</SectionHeader>
      {([
        { code: 'TH' as CountryCode, slug: 'thailand-festivals' },
        { code: 'ID' as CountryCode, slug: 'indonesia-festivals' },
        { code: 'IN' as CountryCode, slug: 'india-festivals' },
      ]).map(({ code, slug }) => (
        <NavDropdownItem
          key={slug}
          icon={'\u{1F3AD}'}
          label={`${getCountryNameEntry(code, lang).display} ${uiFb('ui.festivals', 'Festivals', lang)}`}
          href={`/${slug}`}
          onClick={onClose}
        />
      ))}
      <NavDropdownItem
        icon={'\u{1F3AD}'}
        label={`Bali ${uiFb('ui.festivals', 'Festivals', lang)}`}
        href="/bali-festivals"
        onClick={onClose}
      />
    </NavDropdown>
  );
}
